import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * Last line of defence: a rendering or engine failure must show a readable
 * message with a way out, not a blank page.
 */
export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('WebPaint crashed:', error, info.componentStack);
  }

  private reset = () => this.setState({ error: null });

  override render() {
    const { error } = this.state;
    if (!error) return this.props.children;
    return (
      <div className="flex h-full items-center justify-center p-8">
        <div className="max-w-md rounded-xl border border-danger/50 bg-surface p-5">
          <h1 className="mb-2 text-[15px] font-semibold text-danger">Something broke</h1>
          <p className="mb-3 text-muted">{error.message}</p>
          <pre className="mb-4 max-h-40 overflow-auto rounded bg-app p-2 font-mono text-[11px] text-faint">
            {error.stack ?? ''}
          </pre>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={this.reset}
              className="rounded-md bg-accent px-3 py-1.5 text-[12px] font-medium text-accent-ink"
            >
              Try again
            </button>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="rounded-md border border-line bg-surface-2 px-3 py-1.5 text-[12px] font-medium"
            >
              Reload
            </button>
          </div>
        </div>
      </div>
    );
  }
}
